import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useLazyViewSolarEstimationAdminQuery } from "../../../../redux/api/solar-estimation/solarEstimationAPI";
import {
  IViewSolarEstimationData,
  IViewSolarEstimationOutputParams,
  ViewSolarEstimationResponseDTO,
} from "../../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-response-dto";
import { EApplicationType } from "../../../../shared/oversight-core/enums/application-type";
import { EInverterPhaseType } from "../../../../shared/oversight-core/enums/inverter-phase-type";
import { ESolarSolutionType } from "../../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSystemType } from "../../../../shared/oversight-core/enums/solar-system-type ";
import { EUtilityOptionType } from "../../../../shared/oversight-core/enums/utility-option-type";
import { AppRoute } from "../../../../shared/oversight-core/interfaces/app-routes";
import { Configurations } from "../../../../shared/oversight-core/interfaces/configurations";
import { Option } from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import ArrowButton from "../../../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import SelectedSolarSystemCard from "../../../build-system/components/selected-solar-system-card/selected-solar-system-card";
import SelectedWaterHeaterSystemCard from "../../../build-system/components/selected-water-heater-system-card/selected-water-heater-system-card";
import EditAssembleSystem from "../components/edit-assemble-system/edit-assemble-system";

const solarSystemTypesOption: Option[] = [
  {
    value: ESolarSystemType.BACKUP,
    label: "Backup",
  },
  {
    value: ESolarSystemType.OFF_GRID,
    label: "Off-Grid",
  },
  {
    value: ESolarSystemType.HYBRID,
    label: "Hybrid",
  },
];

const inverterPhaseTypeOptions: Option[] = [
  {
    value: EInverterPhaseType.SINGLE_PHASE,
    label: "1 Phase",
  },
  {
    value: EInverterPhaseType.THREE_PHASE,
    label: "3 Phase",
  },
  {
    value: EInverterPhaseType.NONE,
    label: "None",
  },
];

const applicationTypeOptions: Option[] = [
  {
    value: EApplicationType.RESIDENTIAL,
    label: "Residential",
  },
  {
    value: EApplicationType.HOTELS,
    label: "Hotels",
  },
  {
    value: EApplicationType.HOSTELS,
    label: "Hostels",
  },
  {
    value: EApplicationType.SCHOOLS,
    label: "Schools",
  },
];

const utilityOptions: Option[] = [
  {
    value: EUtilityOptionType.SHOWER,
    label: "Shower",
  },
  {
    value: EUtilityOptionType.BATHTUB_JACUZZI,
    label: "Bathtub/Jacuzzi",
  },
];

const EditEstimation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const estimationId = location.state?.estimationId;

  const [monthlyEnergyNeed, setMonthlyEnergyNeed] = useState(0);
  const [maxPower, setMaxPower] = useState(0);
  const [dischargeRate, setDischargeRate] = useState(50);
  const [nightLoad, setNightLoad] = useState(0);
  const [sunlightHours, setSunlightHours] = useState(5);
  const [waterDemand, setWaterDemand] = useState(0);
  const [assembleSystemData, setAssembleSystemData] = useState<
    IViewSolarEstimationData | undefined
  >(undefined);
  const [isResetRecommendations, setIsResetRecommendations] = useState(false);
  const [selectedSystemOutput, setSelectedSystemOutput] =
    useState<IViewSolarEstimationOutputParams>({
      totalWattHours: 0,
      totalCost: 0,
      systemVoltage: 0,
      totalAmpHours: 0,
      nightLoadAmpHours: 0,
      batteryAmpHours: 0,
      maxPowerInWatt: 0,
      grandTotal: 0,
      totalAccessoriesCost: 0,
      laborCost: 0,
      totalLiters: 0,
    });
  const [submittedUser, setSubmittedUser] = useState("");
  const [estimationTitle, setEstimationTitle] = useState("");
  const [estimationNo, setEstimationNo] = useState(0);
  const [userId, setUserId] = useState("");
  const [configurations, setConfigurations] = useState<Configurations>(
    {} as Configurations
  );
  const [solarSystemType, setSolarSystemType] = useState<Option>({
    ...solarSystemTypesOption[2],
  });
  const [estimationType, setEstimationType] = useState<ESolarSolutionType>(
    ESolarSolutionType.BOTH
  );
  const [applicationType, setApplicationType] = useState<Option>({
    ...applicationTypeOptions[0],
  });
  const [utilityOption, setUtilityOption] = useState<Option>({
    ...utilityOptions[0],
  });
  const [numberOfUsers, setNumberOfUsers] = useState(0);
  const [inverterPhaseType, setInverterPhaseType] = useState<Option>({
    ...inverterPhaseTypeOptions[0],
  });

  const [
    triggerViewSolarEstimationAdmin,
    { isFetching: isFetchingViewSolarEstimationAdmin },
  ] = useLazyViewSolarEstimationAdminQuery();

  useEffect(() => {
    if (estimationId) {
      triggerViewSolarEstimationAdmin({ estimationId })
        .unwrap()
        .then((res: ViewSolarEstimationResponseDTO) => {
          setMonthlyEnergyNeed(
            Number(res.estimationView.data.inputParams.monthlyRequiredEnergy)
          );
          setMaxPower(
            Number(res.estimationView.data.inputParams.maxRequiredPower)
          );
          setDischargeRate(
            Number(res.estimationView.data.inputParams.dischargeRatePercentage)
          );
          setNightLoad(
            Number(res.estimationView.data.inputParams.nightLoadPercentage)
          );
          setSunlightHours(
            Number(res.estimationView.data.inputParams.sunlightHours)
          );
          setWaterDemand(
            Number(res.estimationView.data.inputParams.waterDemand)
          );
          setNumberOfUsers(
            Number(res.estimationView.data.inputParams.numberOfUsers)
          );
          setSelectedSystemOutput(
            res.estimationView.data.outputParams || {
              totalWattHours: 0,
              totalCost: 0,
              systemVoltage: 0,
              totalAmpHours: 0,
              nightLoadAmpHours: 0,
              batteryAmpHours: 0,
              maxPowerInWatt: 0,
              grandTotal: 0,
              totalAccessoriesCost: 0,
              laborCost: 0,
              totalLiters: 0,
            }
          );
          setAssembleSystemData({ ...res.estimationView.data });
          setSubmittedUser(
            res.estimationView.user.firstName +
              " " +
              res.estimationView.user.lastName
          );
          setEstimationTitle(res.estimationView.title || "");
          setEstimationNo(res.estimationView.estimationId);
          setUserId(res.estimationView.user.id);
          setConfigurations(
            res.estimationView.data.preferences as Configurations
          );
          setEstimationType(res.estimationView.estimationType);

          const selectedSolarTypeOption = solarSystemTypesOption.find(
            (s) => s.value === res.estimationView.data.inputParams.systemType
          );

          const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
            (i) => i.value === res.estimationView.inverterPhaseType
          );

          const selectedUtilityOption = utilityOptions.find(
            (u) => u.value === res.estimationView.utilityOptionType
          );

          const selectedApplicationType = applicationTypeOptions.find(
            (a) => a.value === res.estimationView.applicationType
          );

          if (selectedSolarTypeOption) {
            setSolarSystemType({
              value: selectedSolarTypeOption.value,
              label: selectedSolarTypeOption.label,
            });
          }

          if (selectedInverterPhaseTypeOption) {
            setInverterPhaseType({
              value: selectedInverterPhaseTypeOption.value,
              label: selectedInverterPhaseTypeOption.label,
            });
          }

          if (selectedApplicationType) {
            setApplicationType({
              value: selectedApplicationType.value,
              label: selectedApplicationType.label,
            });
          }

          if (selectedUtilityOption) {
            setUtilityOption({
              value: selectedUtilityOption.value,
              label: selectedUtilityOption.label,
            });
          }
        })
        .catch(() => {
          setSelectedSystemOutput({
            totalWattHours: 0,
            totalCost: 0,
            systemVoltage: 0,
            totalAmpHours: 0,
            nightLoadAmpHours: 0,
            batteryAmpHours: 0,
            maxPowerInWatt: 0,
            grandTotal: 0,
            totalAccessoriesCost: 0,
            laborCost: 0,
            totalLiters: 0,
          });
        });
    }
  }, [triggerViewSolarEstimationAdmin, estimationId, isResetRecommendations]);

  return (
    <>
      <Row className="mx-0">
        <Col>
          <Row>
            <Col className="col-auto">
              <ArrowButton
                onBack={() =>
                  navigate(AppRoute.ESTIMATIONS + "/" + estimationId, {
                    state: {
                      estimationId: estimationId,
                      isUserView: false,
                    },
                  })
                }
              />
            </Col>
            <Col className="text-label font-weight-600 font-size-20">
              Edit Estimation
            </Col>
          </Row>
        </Col>
        <Col className="col-auto">
          <AppButton
            text="Continue"
            className="px-4"
            onClick={() =>
              navigate(AppRoute.ESTIMATIONS + "/" + estimationId, {
                state: {
                  estimationId: estimationId,
                  isUserView: false,
                },
              })
            }
          />
        </Col>
      </Row>
      <div className="estimation-container rounded-3">
        <Row className="align-items-center justify-content-between px-3 py-2 mt-4">
          <Col className="col-auto">
            <Row>
              <Col className="text-color-3 font-size-14">#{estimationNo}</Col>
            </Row>
            <Row className="align-items-center">
              <Col className="col-auto title-2 font-weight-600 font-size-18 pe-0">
                {estimationTitle}
              </Col>
            </Row>
          </Col>
          {estimationType === ESolarSolutionType.BOTH ? (
            <Col className="col-12 mt-2">
              <Row className="align-items-stretch gx-2">
                <Col className="col-12 col-xxl-7">
                  <SelectedSolarSystemCard
                    monthlyEnergyNeed={monthlyEnergyNeed}
                    maxPower={maxPower}
                    dischargeRate={dischargeRate}
                    nightLoad={nightLoad}
                    sunlightHours={sunlightHours}
                    monthlyEnergyNeedFromUser={0}
                    maxPowerFromUser={0}
                    totalWattHours={selectedSystemOutput.totalWattHours}
                    maxPowerInWatt={selectedSystemOutput.maxPowerInWatt}
                    isHideEditIcon
                    isHideDeleteIcon
                    solarSystemType={solarSystemType}
                    onChangeSolarSystemType={(option: Option) => {
                      setSolarSystemType(option);
                    }}
                    inverterPhaseType={inverterPhaseType}
                    onChangeInverterPhaseType={(option: Option) => {
                      setInverterPhaseType(option);
                    }}
                  />
                </Col>
                <Col className="col-12 col-xxl-5 mt-3 mt-xxl-0">
                  <SelectedWaterHeaterSystemCard
                    waterDemand={waterDemand}
                    totalLiters={selectedSystemOutput?.totalLiters || 0}
                    numberOfUsers={numberOfUsers}
                    applicationType={applicationType}
                    utilityOption={utilityOption}
                    onApplicationTypeChange={(option: Option) => {
                      setApplicationType(option);
                    }}
                    onUtilityOptionChange={(option: Option) => {
                      setUtilityOption(option);
                    }}
                    isHideEditIcon
                    isHideDeleteIcon
                  />
                </Col>
              </Row>
            </Col>
          ) : estimationType === ESolarSolutionType.SOLAR_ENERGY_SYSTEM ? (
            <Col className="col-12 col-lg-auto mt-3 mt-xxl-0">
              <SelectedSolarSystemCard
                monthlyEnergyNeed={monthlyEnergyNeed}
                maxPower={maxPower}
                dischargeRate={dischargeRate}
                nightLoad={nightLoad}
                sunlightHours={sunlightHours}
                monthlyEnergyNeedFromUser={0}
                maxPowerFromUser={0}
                totalWattHours={selectedSystemOutput.totalWattHours}
                maxPowerInWatt={selectedSystemOutput.maxPowerInWatt}
                isHideEditIcon
                isHideDeleteIcon
                solarSystemType={solarSystemType}
                onChangeSolarSystemType={(option: Option) => {
                  setSolarSystemType(option);
                }}
                inverterPhaseType={inverterPhaseType}
                onChangeInverterPhaseType={(option: Option) => {
                  setInverterPhaseType(option);
                }}
              />
            </Col>
          ) : (
            <Col className="col-12 col-lg-auto mt-3 mt-xxl-0">
              <SelectedWaterHeaterSystemCard
                waterDemand={waterDemand}
                totalLiters={selectedSystemOutput?.totalLiters || 0}
                numberOfUsers={numberOfUsers}
                applicationType={applicationType}
                utilityOption={utilityOption}
                onApplicationTypeChange={(option: Option) => {
                  setApplicationType(option);
                }}
                onUtilityOptionChange={(option: Option) => {
                  setUtilityOption(option);
                }}
                isHideEditIcon
                isHideDeleteIcon
              />
            </Col>
          )}
        </Row>
        <EditAssembleSystem
          assembleSystemData={assembleSystemData}
          estimationId={estimationId}
          selectedSystemOutput={selectedSystemOutput}
          setSelectedSystemOutput={setSelectedSystemOutput}
          setIsResetRecommendations={setIsResetRecommendations}
          submittedUser={submittedUser}
          userId={userId}
          estimationNo={estimationNo}
          configurations={configurations}
          estimationType={estimationType}
        />
      </div>
      <SpinnerModal show={isFetchingViewSolarEstimationAdmin} />
    </>
  );
};

export default EditEstimation;
